import { useEffect, useState } from "react";
import { Trash2, MailOpen } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function AdminMessages() {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/admin/contact-messages").then(({ data }) => setMessages(data)).catch(() => setMessages([]));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.patch(`/admin/contact-messages/${id}/read`);
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/admin/contact-messages/${deleteTarget}`);
    setDeleteTarget(null);
    load();
  };

  return (
    <div data-testid="admin-messages-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-white mb-8">{t("admin.messages.title")}</h1>

      {messages.length === 0 && <p className="text-white/40" data-testid="messages-empty-state">{t("admin.messages.noMessages")}</p>}

      <div className="space-y-4">
        {messages.map((m) => (
          <div key={m.id} data-testid={`message-card-${m.id}`} className={`bg-[#141414] border rounded-lg p-5 ${m.is_read ? "border-white/10" : "border-gold/40"}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-white font-medium flex items-center gap-2">
                  {m.name}
                  {!m.is_read && <span data-testid={`message-unread-badge-${m.id}`} className="text-[10px] uppercase tracking-widest bg-gold/20 text-gold px-2 py-0.5 rounded">{t("admin.messages.unread")}</span>}
                </p>
                <p className="text-white/40 text-xs">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                <p className="text-white/30 text-xs mt-1">{new Date(m.created_at).toLocaleString("nl-NL")}</p>
              </div>
              <div className="flex gap-2">
                {!m.is_read && (
                  <button data-testid={`message-mark-read-${m.id}`} onClick={() => markRead(m.id)} title={t("admin.messages.markRead")} className="text-white/50 hover:text-gold"><MailOpen size={16} /></button>
                )}
                <button data-testid={`message-delete-${m.id}`} onClick={() => setDeleteTarget(m.id)} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="text-white/70 text-sm whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#141414] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.messages.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">{t("admin.messages.deleteConfirmDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="message-delete-cancel" className="bg-transparent border-white/20 text-white hover:bg-white/10">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="message-delete-confirm" onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">{t("common.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
